import apiClient from './client';

export type TerminalType = 'host' | 'container';
export type SessionStatus = 'active' | 'closed' | 'error';

export interface TerminalSession {
  id: string;
  user_id: string;
  server_id: string;
  terminal_type: TerminalType;
  container_name: string | null;
  cols: number;
  rows: number;
  status: SessionStatus;
  started_at: string;
  last_activity_at: string;
  closed_at: string | null;
  command_count: number;
}

export interface TerminalSessionCreate {
  server_id: string;
  terminal_type?: TerminalType;
  container_name?: string;
  cols?: number;
  rows?: number;
}

export interface Container {
  id: string;
  name: string;
  image: string;
  status: string;
}

export const terminalApi = {
  listSessions: async (): Promise<TerminalSession[]> => {
    const response = await apiClient.get<TerminalSession[]>('/api/v1/terminal/sessions');
    return response.data;
  },

  createSession: async (data: TerminalSessionCreate): Promise<TerminalSession> => {
    const response = await apiClient.post<TerminalSession>('/api/v1/terminal/sessions', data);
    return response.data;
  },

  closeSession: async (sessionId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/terminal/sessions/${sessionId}`);
  },

  resizeSession: async (sessionId: string, cols: number, rows: number): Promise<void> => {
    await apiClient.post(`/api/v1/terminal/sessions/${sessionId}/resize`, { cols, rows });
  },

  /**
   * Build the WebSocket URL. The token is passed as a query param because
   * the WebSocket handshake does not support custom headers. To mitigate
   * token logging risks, callers should use short-lived tokens and wss:// in prod.
   */
  getWebSocketUrl: (sessionId: string, token: string): string => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // Use the API host, not the frontend host (proxy-aware)
    const apiBase = import.meta.env.VITE_API_URL || '';
    const wsBase = apiBase
      ? apiBase.replace(/^https?/, protocol.replace(':', ''))
      : `${protocol}//${window.location.host}`;
    return `${wsBase}/api/v1/terminal/ws/${sessionId}?token=${encodeURIComponent(token)}`;
  },

  listContainers: async (serverId: string): Promise<Container[]> => {
    const response = await apiClient.get<{ containers: Container[] }>(`/api/v1/terminal/containers/${serverId}`);
    return response.data.containers;
  },
};
